import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";
import type { Database } from "@/lib/database.types";
import { format } from "date-fns";
import { formatPreviewData, type Channel, type SimplifiedPlan } from "@/utils/previewFormatters";
import { usePlans } from "@/hooks/usePlans";
import { useCategoryHierarchy } from "@/hooks/useCategories";
import { useChannels } from "@/hooks/useChannels";
import { useChannelTypes } from "@/hooks/useChannelTypes";
import { usePackages } from "@/hooks/usePackages";
import { useTags } from "@/hooks/useTags";
import { PreviewBreadcrumb } from "./PreviewBreadcrumb";
import { PDFChannelDocument } from "../pdf/PDFDocument";
import { pdf } from "@react-pdf/renderer";
import { PreviewFilterControls } from "./PreviewFilterControls";
import { usePreviewFilterStore } from "@/stores/previewFilterStore";
import { useCurrencyStore } from "@/stores/currencyStore";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface PreviewHeaderProps {
  provider: {
    id: number;
    name: string;
    has_sequence_numbers: boolean;
  };
  version: Database['public']['Tables']['provider_versions']['Row'] & {
    region_id?: number;
  };
}

export function PreviewHeader({ provider, version }: PreviewHeaderProps) {
  // State for preventing multiple simultaneous PDF generations
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const { data: plans, isLoading: plansLoading } = usePlans(provider.id, version.id);
  const { data: categories, isLoading: categoriesLoading } = useCategoryHierarchy(provider.id, version.id);
  const { data: channels, isLoading: channelsLoading } = useChannels(provider.id, version.id) as { data: Channel[] | undefined; isLoading: boolean };
  const { data: types, isLoading: typesLoading } = useChannelTypes(provider.id, version.id);
  const { packages, isLoading: packagesLoading } = usePackages(provider.id, version.id, version.region_id || 0);
  const { data: tags, isLoading: tagsLoading } = useTags(provider.id, version.id, version.region_id || 0);
  const {
    // Channel filters
    showDescription,
    showPrice,
    showPlans,
    showInAllCategories,
    showLocalChannelsCategory,
    hideSerialNumbers,
    sortOrder,

    // Plan filters
    showPlanDescription,
    showPlanMonthlyPrice,
    showPlanYearlyPrice,

    // Category filters
    showCategoryDescription,
    showCategoryPrice,

    // Package filters
    showPackages,
    showPackageDescription,
    showPackageMonthlyPrice,
    showPackageYearlyPrice,
    showPackageType,

    // Tag filters
    showTags,
    showTagDescription,
    showTagChannelCount
  } = usePreviewFilterStore();

  // Fetch region data if version has a region_id
  const { data: region } = useQuery({
    queryKey: ['version-region', version.region_id],
    queryFn: async () => {
      if (!version.region_id) return null;
      const { data, error } = await supabase
        .from('regions')
        .select('id, name, zip_code')
        .eq('id', version.region_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!version.region_id,
  });

  const filterOptions = {
    // Channel filters
    showDescription,
    showPrice,
    showPlans,
    showInAllCategories,
    showLocalChannelsCategory,
    hideSerialNumbers,
    sortOrder,

    // Plan filters
    showPlanDescription,
    showPlanMonthlyPrice,
    showPlanYearlyPrice,

    // Category filters
    showCategoryDescription,
    showCategoryPrice,

    // Package filters
    showPackages,
    showPackageDescription,
    showPackageMonthlyPrice,
    showPackageYearlyPrice,
    showPackageType,

    // Tag filters
    showTags,
    showTagDescription,
    showTagChannelCount
  };

  const handleCopyToClipboard = async () => {
    try {
      if (!plans || !categories || !channels || !types) {
        throw new Error("Failed to load data");
      }

      const formattedData = formatPreviewData(
        provider,
        version,
        plans,
        categories,
        channels,
        filterOptions,
        types
      );

      // Debug: Check the beginning of the formatted data 
      console.log("WordPress Table Headers:", 
        formattedData.substring(
          formattedData.indexOf("<thead>"), 
          formattedData.indexOf("</thead>") + 8
        )
      );

      await navigator.clipboard.writeText(formattedData);

      toast.success("Table copied in WordPress format");
    } catch (error) {
      toast.error("Failed to copy table");
    }
  };

  const handleDownloadPDF = async () => {
    // Prevent multiple simultaneous PDF generations
    if (isGeneratingPDF) {
      toast.info("Please wait for the current PDF generation to complete.");
      return;
    }

    // Check if any data is still loading
    if (plansLoading || categoriesLoading || channelsLoading || typesLoading || packagesLoading || tagsLoading) {
      toast.info("Data is still loading. Please wait a moment and try again.");
      return;
    }

    // Validate that all required data is available
    if (!plans || !categories || !channels || !types) {
      toast.error("Required data is missing. Please refresh the page and try again.");
      return;
    }

    // Packages and tags are optional, so we'll use empty arrays if not available
    const safePackages = packages || [];
    const safeTags = tags || [];

    // Check for large datasets and provide progress feedback
    const totalDataSize = (plans?.length || 0) + (categories?.length || 0) + (channels?.length || 0) + (types?.length || 0);

    if (totalDataSize > 500) {
      toast.info(`Processing ${channels?.length || 0} channels and ${categories?.length || 0} categories. This may take a moment...`, {
        duration: 3000
      });
    } else if (totalDataSize > 1000) {
      toast.info(`Processing extensive dataset (${totalDataSize} items). Please be patient...`, {
        duration: 5000
      });
    }

    // Set loading state
    setIsGeneratingPDF(true);

    try {
      console.group(`🔄 PDF Generation - Version ${version.id}`);
      console.time('⏱️ Total PDF Generation Time');

      // Validate and safely process plans to prevent array errors
      console.time('Step 1: Validating Plans');
      const validPlans = Array.isArray(plans) ? plans.filter(plan => plan && plan.id) : [];
      console.timeEnd('Step 1: Validating Plans');
      console.log(`✅ Validated ${validPlans.length} plans`);

      console.time('Step 2: Simplifying Plans Data');
      const simplifiedPlans: SimplifiedPlan[] = validPlans.map((plan, index) => {
        if (index % 10 === 0 && index > 0) {
          console.log(`  ↳ Processing plan ${index}/${validPlans.length}...`);
        }

        return {
          id: plan.id,
          name: plan.name || '',
          provider_id: plan.provider_id,
          version_id: plan.version_id,
          monthly_price: plan.monthly_price,
          description: plan.description,
          yearly_price: plan.yearly_price,
          color_index: plan.color_index,
          created_at: plan.created_at,
          channel_count: plan.channel_count,
          // Don't include channels array - it's not used in PDF and causes memory overflow
          channels: undefined
        };
      });
      console.timeEnd('Step 2: Simplifying Plans Data');

      console.log('📊 Dataset Summary:', {
        totalPlans: simplifiedPlans.length,
        totalCategories: categories.length,
        totalChannels: channels.length,
        totalTypes: types.length,
        estimatedComplexity: channels.length > 300 ? '🔴 High' : channels.length > 100 ? '🟡 Medium' : '🟢 Low'
      });

      // Get provider currency outside the PDF component
      console.time('Step 3: Getting Provider Currency');
      const providerCurrency = useCurrencyStore.getState().getProviderCurrency(provider.id);
      console.timeEnd('Step 3: Getting Provider Currency');
      console.log(`✅ Currency: ${providerCurrency}`);

      console.log('🚀 Starting PDF Document Rendering...');
      console.time('Step 4: PDF React Component Creation');
      const doc = await pdf(
        <PDFChannelDocument
          provider={provider}
          version={{
            ...version,
            provider_id: version.provider_id || 0,
            status: (version.status as "draft" | "published") || "draft"
          }}
          region={region ? {
            ...region,
            zip_code: region.zip_code || undefined
          } : null}
          plans={simplifiedPlans}
          categories={categories || []}
          channels={channels || []}
          packages={safePackages}
          tags={safeTags}
          types={types || []}
          providerCurrency={providerCurrency}
          filterOptions={filterOptions}
        />
      ).toBlob();
      console.timeEnd('Step 4: PDF React Component Creation');

      console.time('Step 5: Converting to Blob');
      const blobSize = doc.size;
      console.timeEnd('Step 5: Converting to Blob');
      console.log(`✅ PDF Blob created: ${(blobSize / 1024 / 1024).toFixed(2)} MB`);

      // Create download link
      console.time('Step 6: Creating Download Link');
      const url = URL.createObjectURL(doc);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${provider.name}-v${version.version_number}${region ? `-${region.name}` : ''}-channels.pdf`;
      console.timeEnd('Step 6: Creating Download Link');

      // Trigger download
      console.log('💾 Triggering download...');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);

      console.timeEnd('⏱️ Total PDF Generation Time');
      console.log('✅ PDF generation completed successfully!');
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      console.timeEnd('⏱️ Total PDF Generation Time');

      // Log detailed error info
      if (error instanceof Error) {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }

      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      // Always reset loading state
      setIsGeneratingPDF(false);
      console.groupEnd();
    }
  };

  return (
    <div className="border-b">
      <div className="container mx-auto max-w-[1200px] py-4">
        <PreviewBreadcrumb provider={provider} version={version} />
        
        <div className="mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{provider.name}</h1>
            <div className="text-muted-foreground">
              Version {version.version_number}
              {version.effective_date && (
                <span className="ml-2">
                  (Effective: {format(new Date(version.effective_date), "PP")})
                </span>
              )}
              {region && (
                <span className="ml-2">
                  - Region: {region.name}
                  {region.zip_code && ` (${region.zip_code})`}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleCopyToClipboard}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy as WordPress Table
            </Button>
            
            <Button 
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF || plansLoading || categoriesLoading || channelsLoading || typesLoading}
            >
              <Download className="mr-2 h-4 w-4" />
              {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
            </Button>
          </div>
        </div>
        
        <div className="mt-4">
          <PreviewFilterControls />
        </div>
      </div>
    </div>
  );
}